import type { FacilityId } from './facilities'
import type { SpaceMissionId } from './spaceMissions'
import type {
  TechnologyBranch,
  TechnologyId,
} from './technologies'
import {
  MAJOR_POWER_IDS,
  getNationDefinition,
  type MajorPowerId,
  type NationAvailability,
} from './nations'
import { getNationProfile } from './nationProfiles'

export interface NationArchiveMissionModifier {
  missionId: SpaceMissionId
  turns: number
}

export interface NationArchiveEntry {
  id: MajorPowerId
  displayName: string
  availability: NationAvailability
  routeName: string
  routeSummary: string
  doctrineSummary: string
  traitSlots: number
  effectLabels: string[]
  focusBranches: TechnologyBranch[]
  preferredTechnologies: TechnologyId[]
  facilityPriorities: FacilityId[]
  intenseFacilityPriorities: FacilityId[]
  researchPriorities: TechnologyId[]
  intenseResearchPriorities: TechnologyId[]
  missionModifiers: NationArchiveMissionModifier[]
  aiEnabled: boolean
  strategyId: MajorPowerId | null
}

export function getNationArchiveEntries(): NationArchiveEntry[] {
  return MAJOR_POWER_IDS.map((nationId) =>
    getNationArchiveEntry(nationId),
  )
}

export function getNationArchiveEntry(
  nationId: MajorPowerId,
): NationArchiveEntry {
  const nation = getNationDefinition(nationId)
  const profile = getNationProfile(nationId)
  const missionModifiers = Object.entries(
    profile.missionTurnModifiers,
  )
    .filter((entry): entry is [SpaceMissionId, number] =>
      typeof entry[1] === 'number',
    )
    .map(([missionId, turns]) => ({
      missionId,
      turns,
    }))

  return {
    id: nationId,
    displayName: nation.displayName,
    availability: nation.availability,
    routeName: nation.route.name,
    routeSummary: nation.route.summary,
    doctrineSummary: profile.summary,
    traitSlots: nation.route.traitSlots,
    effectLabels: [...profile.effectLabels],
    focusBranches: [...nation.techProfile.focusBranches],
    preferredTechnologies: [...nation.techProfile.preferredTechnologies],
    facilityPriorities: [...profile.aiFacilityPriorities],
    intenseFacilityPriorities: [...profile.aiIntenseFacilityPriorities],
    researchPriorities: [...profile.aiResearchPriorities],
    intenseResearchPriorities: [...profile.aiIntenseResearchPriorities],
    missionModifiers,
    aiEnabled: nation.ai.enabled,
    strategyId: nation.ai.strategyId,
  }
}
